import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { DestinationCommandeLabels } from "../models/destinationCommande.map";
import { StatutCommandeLabels } from "../models/commandeStatut.map";
import { SourceCommandeLabels } from "../models/sourceCommande.map";
import ReplyMailModal from "./ReplyMailModal";
import SendToEhpadModal from "./SendToEhpadModal";
import MailMessageContent from "./MailMessageContent";
import { API, assignEhpad, addEmailToEhpad, addEmailToEhpadAsFax } from "../services/api";
import { isFaxSender, isPharmacyAuthoredMessage, isPortalConversation } from "../utils/commandeMail";

const STATUT_CONFIG = {
    A_VALIDER: { bg: "#fff0bd", color: "#65470e", dot: "#c8a44d" },
    VALIDE:    { bg: "#dff5e9", color: "#08784a", dot: "#17b978" },
};

const headerControlGroupStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    flex: "0 0 auto",
    minWidth: 0,
};

const headerSelectStyle = {
    padding: "4px 8px",
    borderRadius: "6px",
    border: "1px solid #a9b8b2",
    background: "#fffdfa",
    cursor: "pointer",
    fontSize: "13px",
    color: "#122a26",
    width: "150px",
    maxWidth: "150px",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
};

function formatShortDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function formatDateTime(dateStr) {
    const d = new Date(dateStr);
    const months = ["jan.", "fév.", "mars", "avr.", "mai", "juin", "juil.", "août", "sep.", "oct.", "nov.", "déc."];
    return `${d.getDate()} ${months[d.getMonth()]} · ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function StarButton({ active, onClick }) {
    return (
        <button
            onClick={onClick}
            title={active ? "Retirer des favoris" : "Ajouter aux favoris"}
            style={{
                background: "none", border: "none", cursor: "pointer",
                padding: "4px", borderRadius: "4px",
                color: active ? "#f59e0b" : "#cbd5e1",
                fontSize: "18px", lineHeight: 1,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "color 0.1s", flexShrink: 0,
            }}
            onMouseEnter={e => { if (!active) e.currentTarget.style.color = "#fbbf24"; }}
            onMouseLeave={e => { if (!active) e.currentTarget.style.color = active ? "#f59e0b" : "#cbd5e1"; }}
        >
            {active ? "★" : "☆"}
        </button>
    );
}

function getEhpadIdentity(commande) {
    const code = commande.ehpad?.code || commande.ehpadCode || "";
    let fullName = commande.ehpad?.nom || "";
    if (!fullName && commande.ehpadNom !== "AUTRE") {
        fullName = commande.ehpadNom || "";
    }
    return { code, fullName, displayName: fullName || code };
}

function ehpadSelectLabel(ehpad) {
    return [ehpad?.code, ehpad?.nom].filter(Boolean).join(" — ");
}

export default function CommandeDetail({ commande, draft, setDraft, onSelectOrdonnance, onBack, extraActions, onUpdate, onFavoriteChange, lockStatut = false, noPharmaceuticalValidation = false, allowWorkflowActionsWhenLocked = false, isDirty = false, onSave, onDelete, lockedByUser = null, lockNotice = null, autoSaveStatus = "idle", saveErrors = [] }) {
    const [statut, setStatut] = useState(commande?.statut || "");
    const [showReplyModal, setShowReplyModal] = useState(false);
    const [showSendToEhpadModal, setShowSendToEhpadModal] = useState(false);
    const [ehpads, setEhpads] = useState([]);
    const [assigningEhpad, setAssigningEhpad] = useState(false);
    const [emailPrompt, setEmailPrompt] = useState(null); // { ehpad }
    const [emailPromptSaving, setEmailPromptSaving] = useState(false);
    const [deletingMessageId, setDeletingMessageId] = useState(null);
    const [hoveredMessageId, setHoveredMessageId] = useState(null);

    const handleDeleteMessage = async (msg) => {
        const confirmed = globalThis.confirm("Supprimer ce message ?");
        if (confirmed) {
            setDeletingMessageId(msg.id);
            try {
                await API.delete(`/commandes/${commande.id}/messages/${msg.id}`);
                setDraft(prev => prev ? { ...prev, messages: prev.messages.filter(m => m.id !== msg.id) } : prev);
            } catch {
                alert("Erreur lors de la suppression du message.");
            } finally {
                setDeletingMessageId(null);
            }
        }
    };

    useEffect(() => {
        if (commande) {
            setStatut(commande.statut || "");
        }
    }, [commande]);

    const handleToggleFavorite = async () => {
        const nextFavorite = !(draft?.favorite ?? false);
        if (onFavoriteChange) {
            try {
                await onFavoriteChange(commande.id, nextFavorite);
            } catch {
                alert("Erreur lors de la mise à jour du favori.");
            }
            return;
        }
        setDraft(prev => ({ ...prev, favorite: nextFavorite }));
    };

    useEffect(() => {
        API.get("/ehpads").then(r => setEhpads(r.data)).catch(() => {});
    }, []);

    if (!commande) return null;

    const handleEhpadSelect = async (ehpadId) => {
        if (!ehpadId) return;
        setAssigningEhpad(true);
        try {
            let selected = ehpads.find(e => e.id === Number(ehpadId));
            const { data } = await assignEhpad(commande.id, Number(ehpadId));
            if (!selected && data?.ehpad) selected = data.ehpad;
            if (commande.emailEmetteur && selected) {
                setEmailPrompt({ ehpad: selected });
                try {
                    const { data: assocData } = await API.get("/ehpads/by-email", { params: { email: commande.emailEmetteur }, noRedirectOn401: true });
                    if (assocData.some(a => a.id === Number(ehpadId))) setEmailPrompt(null);
                } catch { /* garder la proposition si la vérification échoue */ }
            }
            if (onUpdate) onUpdate(data);
        } catch {
            setEmailPrompt(null);
            // assignEhpad failure is silent — state already unchanged
        } finally {
            setAssigningEhpad(false);
        }
    };

    const handleEmailPromptConfirm = async (type) => {
        if (!emailPrompt) return;
        setEmailPromptSaving(true);
        try {
            const addFn = type === "fax" ? addEmailToEhpadAsFax : addEmailToEhpad;
            await addFn(emailPrompt.ehpad.id, commande.emailEmetteur);
        } finally {
            setEmailPromptSaving(false);
            setEmailPrompt(null);
        }
    };

    const files = draft?.files || [];
    const { displayName: ehpadName } = getEhpadIdentity(commande);
    const selectableEhpads = commande.ehpad?.id && !ehpads.some(ehpad => ehpad.id === commande.ehpad.id)
        ? [commande.ehpad, ...ehpads]
        : ehpads;
    const supplierName = commande.medicalEquipmentSupplier?.name || "";
    const senderLabel = supplierName || ehpadName;
    const replyDisabledForFax = isFaxSender(commande);
    const portalConversation = isPortalConversation(commande);
    const handleSendToEhpadClose = async (sent) => {
        setShowSendToEhpadModal(false);
        if (!sent) return;
        try {
            const { data } = await API.get(`/commandes/${commande.id}`, { noRedirectOn401: true });
            setDraft(previous => previous ? { ...previous, messages: data.messages ?? previous.messages } : previous);
        } catch {
            // Le message est envoyé ; la conversation sera resynchronisée au prochain chargement.
        }
    };
    const autoSaveLabels = {
        local: { text: "Brouillon protégé", color: "#0369a1", bg: "#e0f2fe", border: "#bae6fd" },
        saving: { text: "Sauvegarde...", color: "#7c2d12", bg: "#ffedd5", border: "#fed7aa" },
        saved: { text: "Sauvegardé", color: "#166534", bg: "#dcfce7", border: "#bbf7d0" },
        partial: { text: "Sauvegarde partielle", color: "#92400e", bg: "#fef3c7", border: "#fbbf24" },
        error: { text: "Sauvegarde à relancer", color: "#991b1b", bg: "#fee2e2", border: "#fecaca" },
    };
    const autoSaveInfo = autoSaveLabels[autoSaveStatus];

    const handleStatutChange = (newStatut) => {
        if (newStatut === statut) return;
        setStatut(newStatut);
        setDraft(prev => ({ ...prev, statut: newStatut }));
    };

    return (
        <>
        <div className="commande-detail-shell" style={{ display: "flex", flexDirection: "column", flexShrink: 0, background: "white" }}>

            {/* Bannière de lock multi-utilisateur */}
            {(lockedByUser || lockNotice) && (
                <div className="commande-detail-lock-notice" style={{
                    padding: "8px 20px",
                    background: "#fef3c7",
                    borderBottom: "1px solid #fbbf24",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    color: "#92400e",
                    fontWeight: 500,
                    flexShrink: 0,
                }}>
                    <span style={{ fontSize: "15px" }}>🔒</span>
                    {lockedByUser ? (
                        <>
                            Ce mail est en cours d'édition par un autre utilisateur{" "}
                            <strong style={{ marginLeft: "3px" }}>({lockedByUser})</strong>.
                        </>
                    ) : lockNotice}
                    <span style={{ color: "#b45309", fontWeight: 400, marginLeft: "4px" }}>
                        Les modifications sont désactivées.
                        {allowWorkflowActionsWhenLocked ? " Les validations restent disponibles." : ""}
                    </span>
                </div>
            )}

            {saveErrors.length > 0 && (
                <div className="commande-detail-error-notice" style={{
                    padding: "10px 20px",
                    background: "#fff7ed",
                    borderBottom: "1px solid #fdba74",
                    color: "#9a3412",
                    fontSize: "13px",
                }}>
                    <strong>
                        {autoSaveStatus === "partial"
                            ? "Les autres patients ont été sauvegardés, mais :"
                            : autoSaveStatus === "local"
                                ? "Ce patient reste dans le brouillon :"
                                : "La sauvegarde a échoué :"}
                    </strong>
                    <ul style={{ margin: "5px 0 0", paddingLeft: "20px" }}>
                        {saveErrors.map((error, index) => <li key={`${error}-${index}`}>{error}</li>)}
                    </ul>
                </div>
            )}

            {/* Breadcrumb + status bar */}
            <div className="commande-detail-toolbar" style={{
                padding: "10px 20px",
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                flexShrink: 0,
                flexWrap: "wrap",
                overflow: "hidden",
            }}>
                {/* Bouton retour + Breadcrumb */}
                <div className="commande-detail-breadcrumb" style={{ display: "flex", alignItems: "center", gap: "8px", flex: "1 1 360px", minWidth: 0, overflow: "hidden" }}>
                    <button
                        className="commande-detail-back"
                        onClick={onBack}
                        style={{
                            padding: "5px 12px",
                            borderRadius: "7px",
                            border: "1px solid #e2e8f0",
                            background: "white",
                            cursor: "pointer",
                            fontSize: "13px",
                            color: "#475569",
                            fontWeight: 500,
                            display: "flex", alignItems: "center", gap: "5px",
                            flexShrink: 0,
                        }}
                    >
                        ← Retour à la liste
                    </button>
                    <span style={{ color: "#e2e8f0" }}>/</span>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#0f172a", whiteSpace: "nowrap" }}>
                        #{commande.id}
                    </span>
                    <select
                        className={`commande-detail-ehpad-select${commande.ehpad?.id ? "" : " commande-detail-ehpad-select--empty"}`}
                        aria-label="EHPAD de la commande"
                        value={commande.ehpad?.id || ""}
                        disabled={assigningEhpad || selectableEhpads.length === 0}
                        onChange={e => handleEhpadSelect(e.target.value)}
                        title={ehpadSelectLabel(commande.ehpad) || commande.ehpadNom || ""}
                    >
                        {!commande.ehpad?.id && <option value="" disabled>— Associer un EHPAD —</option>}
                        {selectableEhpads.map(e => (
                            <option key={e.id} value={e.id}>{ehpadSelectLabel(e)}</option>
                        ))}
                    </select>
                </div>

                {/* Destination */}
                <div className="commande-detail-control" style={headerControlGroupStyle}>
                    <span style={{ fontSize: "12px", color: "#94a3b8" }}>Dest.</span>
                    <select
                        value={draft?.destination || "NONE"}
                        onChange={(e) => setDraft(prev => ({ ...prev, destination: e.target.value }))}
                        style={headerSelectStyle}
                    >
                        {Object.entries(DestinationCommandeLabels).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </div>

                {/* Source de la commande */}
                <div className="commande-detail-control" style={headerControlGroupStyle}>
                    <span style={{ fontSize: "12px", color: "#94a3b8" }}>Source</span>
                    <select
                        value={draft?.source || "AUTRE"}
                        onChange={(e) => setDraft(prev => ({ ...prev, source: e.target.value }))}
                        style={headerSelectStyle}
                    >
                        {Object.entries(SourceCommandeLabels).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </div>

                {/* Status pills — masqués si le statut est géré par un autre onglet */}
                {!lockStatut && (
                    <div className="commande-detail-statuses" style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                        {Object.entries(STATUT_CONFIG).map(([key, { bg, color, dot }]) => {
                            const active = statut === key;
                            return (
                                <button
                                    key={key}
                                    onClick={() => handleStatutChange(key)}
                                    style={{
                                        padding: "4px 12px",
                                        borderRadius: "999px",
                                        border: `1px solid ${active ? color : "transparent"}`,
                                        background: active ? bg : "transparent",
                                        color: active ? color : "#94a3b8",
                                        fontWeight: active ? 600 : 400,
                                        fontSize: "12px",
                                        cursor: "pointer",
                                        display: "flex", alignItems: "center", gap: "5px",
                                        transition: "all 0.15s",
                                    }}
                                >
                                    {active && <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: dot, display: "inline-block" }} />}
                                    {StatutCommandeLabels[key]}
                                </button>
                            );
                        })}
                    </div>
                )}

                {extraActions && (
                    <div style={{ flexShrink: 0, marginLeft: "8px" }}>
                        {extraActions}
                    </div>
                )}

                {autoSaveInfo && (
                    <span
                        aria-live="polite"
                        style={{
                            padding: "4px 9px",
                            borderRadius: "999px",
                            border: `1px solid ${autoSaveInfo.border}`,
                            background: autoSaveInfo.bg,
                            color: autoSaveInfo.color,
                            fontSize: "12px",
                            fontWeight: 600,
                            flexShrink: 0,
                        }}
                    >
                        {autoSaveInfo.text}
                    </span>
                )}

                <StarButton active={draft?.favorite ?? false} onClick={handleToggleFavorite} />

                {onDelete && (
                    <button
                        onClick={() => onDelete(commande)}
                        title="Supprimer ce mail"
                        style={{
                            background: "none", border: "none", cursor: "pointer",
                            padding: "4px", borderRadius: "4px",
                            color: "#cbd5e1", fontSize: "16px", lineHeight: 1,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            transition: "color 0.1s", flexShrink: 0,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = "#ef4444"; }}
                        onMouseLeave={e => { e.currentTarget.style.color = "#cbd5e1"; }}
                    >
                        🗑
                    </button>
                )}
            </div>

            {!noPharmaceuticalValidation && isDirty && commande.statut === "VALIDE" && (
                <div className="commande-detail-warning" style={{
                    padding: "8px 20px",
                    background: "#fffbeb",
                    borderBottom: "1px solid #fde68a",
                    color: "#92400e",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    flexShrink: 0,
                }}>
                    <span>⚠</span>
                    <span>Cette commande est validée. Toute modification clinique remettra l’élément concerné et la commande en <strong>À valider</strong>, sans perdre les validations des autres éléments.</span>
                </div>
            )}

            {/* Mail + Pièces jointes */}
            <div className="commande-detail-mail-area" style={{
                display: "flex",
                gap: "14px",
                padding: "14px 20px",
                borderBottom: "1px solid #f1f5f9",
                flexShrink: 0,
                background: "#f8fafc",
            }}>
                {/* Fil de messages */}
                <div className="commande-detail-thread" style={{
                    flex: 1,
                    background: "white",
                    borderRadius: "10px",
                    border: "1px solid #e2e8f0",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 0,
                    overflow: "hidden",
                }}>
                    {/* En-tête */}
                    <div className="commande-detail-thread-header" style={{ padding: "10px 14px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="commande-detail-subject-line" style={{ display: "flex", alignItems: "baseline", gap: "8px", minWidth: 0, fontWeight: 600, fontSize: "14px", color: "#0f172a", whiteSpace: "nowrap" }}>
                                {commande.dateReception && (
                                    <span className="commande-detail-received-at" style={{ flexShrink: 0, fontFamily: "Manrope, Arial, sans-serif", fontSize: "12px", fontWeight: 700, color: "#5a6d68" }}>
                                        Reçue {formatShortDate(commande.dateReception)} ·
                                    </span>
                                )}
                                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {commande.commentaire || "(sans objet)"}
                                </span>
                            </div>
                            {commande.emailEmetteur && (
                                <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
                                    {senderLabel && <span>{senderLabel} · </span>}
                                    <span>{commande.emailEmetteur}</span>
                                </div>
                            )}
                        </div>
                        <div className="commande-detail-thread-tools">
                            <span className="commande-detail-message-count">
                                {(draft?.messages || []).length} message{(draft?.messages || []).length !== 1 ? "s" : ""}
                            </span>
                            {(commande.emailEmetteur || commande.ehpad) && (
                                <div className="commande-detail-mail-actions">
                                    {commande.emailEmetteur && (
                                        <button
                                            type="button"
                                            className="commande-detail-reply-action"
                                            onClick={() => setShowReplyModal(true)}
                                            disabled={replyDisabledForFax}
                                            title={replyDisabledForFax ? "Réponse désactivée pour une adresse fax" : (portalConversation ? "Répondre dans le portail EHPAD" : "Répondre au mail")}
                                        >
                                            ↩ {portalConversation ? "Répondre sur le portail" : "Répondre"}
                                        </button>
                                    )}
                                    {commande.ehpad && (
                                        <button
                                            type="button"
                                            className="commande-detail-write-action"
                                            onClick={() => setShowSendToEhpadModal(true)}
                                        >
                                            ✉ Écrire à l'EHPAD
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Messages */}
                    <div className="commande-detail-messages" style={{
                        overflowY: "auto",
                        padding: "10px 14px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        maxHeight: "220px",
                    }}>
                        {(draft?.messages?.length ?? 0) === 0 ? (
                            <span style={{ fontSize: "13px", color: "#94a3b8" }}>(aucun message)</span>
                        ) : draft.messages.map((msg, i) => {
                            const isReply = isPharmacyAuthoredMessage(msg);
                            const incomingLabel = commande.source === "PORTAIL_COLLECTIVITE"
                                ? "Portail collectivité"
                                : (supplierName || commande.ehpadNom || commande.emailEmetteur || "EHPAD");
                            const messageKey = msg.id ?? `${msg.source ?? "message"}-${msg.dateMessage ?? msg.sujet ?? msg.contenu ?? "unknown"}`;
                            const isHovered = hoveredMessageId === messageKey;
                            const isDeleting = deletingMessageId === msg.id;
                            const mailLink = msg.mailLink ?? msg.gmailLink ?? null;
                            return (
                                <div
                                    key={messageKey}
                                    style={{ display: "flex", flexDirection: "column", alignItems: isReply ? "flex-end" : "flex-start" }}
                                    onMouseEnter={() => setHoveredMessageId(messageKey)}
                                    onMouseLeave={() => setHoveredMessageId(null)}
                                >
                                    <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "3px", display: "flex", alignItems: "center", gap: "8px" }}>
                                        {isReply ? "Vous" : incomingLabel}
                                        {msg.dateMessage && (
                                            <span>{formatDateTime(msg.dateMessage)}</span>
                                        )}
                                        {mailLink && (
                                            <a
                                                href={mailLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={msg.mailProvider === "ORANGE" ? "Voir dans Orange Mail" : "Voir dans le mail"}
                                                style={{ color: "#2563eb", fontSize: "11px", textDecoration: "none", lineHeight: 1 }}
                                            >
                                                ✉ Mail
                                            </a>
                                        )}
                                        {isHovered && msg.id && (
                                            <button
                                                onClick={() => handleDeleteMessage(msg)}
                                                disabled={isDeleting}
                                                title="Supprimer ce message"
                                                style={{
                                                    background: "none", border: "none", padding: "1px 4px",
                                                    cursor: "pointer", color: "#ef4444", fontSize: "12px",
                                                    borderRadius: "4px", lineHeight: 1,
                                                    opacity: isDeleting ? 0.5 : 1,
                                                }}
                                            >
                                                🗑
                                            </button>
                                        )}
                                    </div>
                                    <div className={`commande-detail-message${isReply ? " commande-detail-message--reply" : ""}`} style={{
                                        maxWidth: "85%",
                                        padding: "7px 12px",
                                        borderRadius: isReply ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                                        background: isReply ? "#eff6ff" : "#f1f5f9",
                                        border: isReply ? "1px solid #bfdbfe" : "1px solid #e2e8f0",
                                        fontSize: "13px", color: "#334155",
                                        whiteSpace: msg.contenuHtml ? "normal" : "pre-wrap",
                                        lineHeight: "1.5",
                                        wordBreak: "break-word",
                                        opacity: isDeleting ? 0.4 : 1,
                                        transition: "opacity 0.2s",
                                    }}>
                                        {msg.sujet && isReply && (
                                            <div style={{ fontWeight: 600, marginBottom: "3px", color: "#1e40af", fontSize: "12px" }}>
                                                {msg.sujet}
                                            </div>
                                        )}
                                        <MailMessageContent html={msg.contenuHtml} text={msg.contenu} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                </div>

                {/* Pièces jointes */}
                {files.length > 0 && (
                    <div className="commande-detail-attachments" style={{
                        width: "200px", minWidth: "200px",
                        background: "white",
                        borderRadius: "10px",
                        border: "1px solid #e2e8f0",
                        padding: "12px",
                        display: "flex", flexDirection: "column", gap: "6px",
                    }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Pièces jointes ({files.length})
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "4px", overflowY: "auto", maxHeight: "180px" }}>
                            {files.map((f, i) => {
                                const fileKey = f.id ?? `${f.name || "fichier"}-${f.url || f.path || f.filePath || "piece-jointe"}`;
                                return (
                                <button
                                    className="commande-detail-attachment"
                                    type="button"
                                    key={fileKey}
                                    onClick={() => onSelectOrdonnance?.(f)}
                                    style={{
                                        width: "100%",
                                        padding: "6px 10px",
                                        background: "#f8fafc", borderRadius: "6px",
                                        border: "1px solid #e2e8f0",
                                        fontSize: "12px", color: "#2563eb",
                                        cursor: "pointer",
                                        display: "flex", alignItems: "center", gap: "6px",
                                        overflow: "hidden",
                                        textAlign: "left",
                                    }}
                                >
                                    <span style={{ color: "#94a3b8", fontWeight: 700, flexShrink: 0, fontSize: "11px" }}>{i + 1}</span>
                                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {f.name || "Fichier"}
                                    </span>
                                </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

        </div>

        {showReplyModal && (
            <ReplyMailModal commande={commande} onClose={() => setShowReplyModal(false)} />
        )}
        {showSendToEhpadModal && (
            <SendToEhpadModal commande={commande} onClose={handleSendToEhpadClose} />
        )}
        {emailPrompt && commande.emailEmetteur && (
            <div
                className="commande-email-association-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="commande-email-association-title"
                aria-describedby="commande-email-association-description"
            >
                <div className="commande-email-association-modal__sheet" aria-busy={emailPromptSaving}>
                    <header className="commande-email-association-modal__header">
                        <span className="commande-email-association-modal__icon" aria-hidden="true">✉</span>
                        <div>
                            <span className="commande-email-association-modal__eyebrow">Association EHPAD</span>
                            <h2 id="commande-email-association-title">
                                Ajouter l'expéditeur à {ehpadSelectLabel(emailPrompt.ehpad)} ?
                            </h2>
                        </div>
                    </header>
                    <div className="commande-email-association-modal__body">
                        <div className="commande-email-association-modal__email">
                            <span>Expéditeur</span>
                            <strong>{commande.emailEmetteur}</strong>
                        </div>
                        <p id="commande-email-association-description">
                            Souhaitez-vous associer cet expéditeur à cet EHPAD pour la classification automatique ?
                        </p>
                    </div>
                    <footer className="commande-email-association-modal__actions">
                        <button
                            type="button"
                            className="commande-email-association-modal__primary"
                            onClick={() => handleEmailPromptConfirm("contact")}
                            disabled={emailPromptSaving}
                            autoFocus
                        >
                            Contact
                        </button>
                        <button
                            type="button"
                            className="commande-email-association-modal__secondary"
                            onClick={() => handleEmailPromptConfirm("fax")}
                            disabled={emailPromptSaving}
                        >
                            Fax
                        </button>
                        <button
                            type="button"
                            className="commande-email-association-modal__dismiss"
                            onClick={() => setEmailPrompt(null)}
                            disabled={emailPromptSaving}
                        >
                            Non
                        </button>
                    </footer>
                </div>
            </div>
        )}
        </>
    );
}

const ehpadShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    code: PropTypes.string,
    nom: PropTypes.string,
    emailsFax: PropTypes.arrayOf(PropTypes.string),
});

const messageShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    source: PropTypes.string,
    dateMessage: PropTypes.string,
    gmailLink: PropTypes.string,
    mailLink: PropTypes.string,
    mailProvider: PropTypes.string,
    sujet: PropTypes.string,
    contenu: PropTypes.string,
    contenuHtml: PropTypes.string,
});

const fileShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    url: PropTypes.string,
    path: PropTypes.string,
    filePath: PropTypes.string,
});

const commandeShape = PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    statut: PropTypes.string,
    emailEmetteur: PropTypes.string,
    ehpad: ehpadShape,
    ehpadNom: PropTypes.string,
    medicalEquipmentSupplier: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        name: PropTypes.string,
        email: PropTypes.string,
    }),
    commentaire: PropTypes.string,
    dateReception: PropTypes.string,
});

const draftShape = PropTypes.shape({
    favorite: PropTypes.bool,
    destination: PropTypes.string,
    source: PropTypes.string,
    messages: PropTypes.arrayOf(messageShape),
    files: PropTypes.arrayOf(fileShape),
});

StarButton.propTypes = {
    active: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
};

CommandeDetail.propTypes = {
    commande: commandeShape,
    draft: draftShape,
    setDraft: PropTypes.func.isRequired,
    onSelectOrdonnance: PropTypes.func,
    onBack: PropTypes.func.isRequired,
    extraActions: PropTypes.node,
    onUpdate: PropTypes.func,
    onFavoriteChange: PropTypes.func,
    lockStatut: PropTypes.bool,
    noPharmaceuticalValidation: PropTypes.bool,
    isDirty: PropTypes.bool,
    onSave: PropTypes.func,
    onDelete: PropTypes.func,
    lockedByUser: PropTypes.string,
    allowWorkflowActionsWhenLocked: PropTypes.bool,
    lockNotice: PropTypes.string,
    autoSaveStatus: PropTypes.string,
    saveErrors: PropTypes.arrayOf(PropTypes.string),
};
